import moment from "moment";
import {timeChangeReasons, courierChangeReasons} from "./PoolShiftChangeModals";
import {toShiftStatusLabel} from "../../../constants/shiftStatuses";
import {isPoolWorkType} from "../../../constants/shiftWorkTypes";

// Havuz vardiyaları, dedike vardiyalar ve puantaj raporları aynı tabloda gösterildiği için üç
// farklı response tek satır şemasına indirgeniyor. Tablo yalnızca bu şemayı bilir; aksiyon
// butonları orijinal kaydı `raw` üzerinden mevcut modallere aynı payload ile geçirmeye devam
// eder.

const changeReasonLabels = new Map(
    [...timeChangeReasons, ...courierChangeReasons].map(reason => [reason.value, reason.label])
);

export const getChangeReasonLabel = reason => changeReasonLabels.get(reason) || reason || null;

/* "08sa 30dk" → 510. Havuz ve vardiya uçları süreleri bu etiketle döndürüyor. */
const durationLabelToMinute = value => {
    if (!value) return null;
    const parsed = String(value).match(/(\d+)\s*sa\s*(\d+)\s*dk/);
    if (!parsed) return null;
    return Number(parsed[1]) * 60 + Number(parsed[2]);
};

/* Paket sayıları uçtan sayı, "-" veya boş gelebiliyor; tabloda 0 basılabilmesi için sayı
   olmayan değerler null'a düşürülüyor. */
const toCount = value => {
    if (value === null || value === undefined || value === "") return null;
    const count = Number(value);
    return Number.isFinite(count) ? count : null;
};

/* Vardiya ucundaki planlanan saatler "YYYY-MM-DD HH:mm:ss" olarak geliyor. */
const toShiftClock = value => {
    if (!value) return null;
    const parsed = moment(value, "YYYY-MM-DD HH:mm:ss");
    return parsed.isValid() ? parsed.format("HH:mm") : null;
};

/* Gerçekleşen saat, puantaj raporu oluşmuşsa "DD-MM-YYYY HH:mm:ss", oluşmamışsa doğrudan
   "HH:mm" geliyor. */
const toActualClock = value => {
    if (!value) return null;
    const text = String(value).trim();
    if (/^\d{2}:\d{2}$/.test(text)) return text;
    const parsed = moment(text, "DD-MM-YYYY HH:mm:ss");
    return parsed.isValid() ? parsed.format("HH:mm") : null;
};

/* Devam eden vardiyanın puantaj raporu henüz oluşmadığı için çalışma süresi, girişten bu yana
   geçen süredir. Giriş saati uçtan "DD-MM-YYYY HH:mm:ss" olarak geliyor. */
const elapsedWorkMinute = startTime => {
    if (!startTime) return null;
    const start = moment(startTime, "DD-MM-YYYY HH:mm:ss");
    return start.isValid() ? moment().diff(start, "minutes") : null;
};

/* Vardiya bitişi başlangıcından küçükse ertesi güne sarkmıştır. */
const shiftDurationToMinute = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    const start = moment(startTime, "HH:mm");
    const end = moment(endTime, "HH:mm");
    if (!start.isValid() || !end.isValid()) return null;
    if (end.isBefore(start)) end.add(1, "day");
    return end.diff(start, "minutes");
};

/* Havuz tarafı boş gerçekleşen saati " - " olarak döndürüyor. */
const cleanTimeRange = value => {
    const cleaned = String(value || "").trim();
    return cleaned === "-" || cleaned === "" ? null : cleaned;
};

const joinTimeRange = (startTime, endTime) => {
    if (!startTime && !endTime) return null;
    return `${startTime || ""} - ${endTime || ""}`.trim();
};

const normalizePoolShiftRow = row => ({
    // Havuz satırlarında `_id` dönmüyor; tablo için stabil bir anahtar üretiyoruz.
    rowKey: [row.date, row.shiftId, row.courier?._id, row.plannedStartTime].join("_"),
    date: row.date ? moment(row.date, "YYYY-MM-DD").format("DD.MM.YYYY") : null,
    // Sabit entegre havuzları tek şubeye bağlı olduğu için firma-şube adı geliyor; paylaşımlı
    // havuzda tek bir şube adı anlamlı olmadığından havuz adı gösterilir.
    projectName:
        [row.companyName, row.subCompanyName].filter(Boolean).join(" - ") ||
        row.poolName?.trim() ||
        null,
    courier: {
        _id: row.courier?._id,
        fullname: row.courier?.name,
        tckn: row.courier?.tckn
    },
    city: row.city,
    plannedTime: cleanTimeRange(row.plannedTime),
    actualTime: cleanTimeRange(row.actualTime),
    status: toShiftStatusLabel(row.status),
    plannedWorkTimeMinute: durationLabelToMinute(row.plannedWorkingHours),
    workTimeMinute: durationLabelToMinute(row.workingHours),
    changeReason: getChangeReasonLabel(row.cancelNote),
    packageCount: toCount(row.packageCount),
    // Havuz ve sabit entegre uçları onaylanmış paket sayısını ayrı bir alan olarak döndürmüyor:
    // bu vardiyalarda paket sayısı entegrasyondan geldiği gibi onaylı kabul edildiği için
    // kolonda paket sayısının kendisi gösteriliyor. Uç alanı göndermeye başlarsa o kazanır.
    verifiedPackageCount: toCount(row.verifiedPackageCount) ?? toCount(row.packageCount),
    isActiveShift: ["active_courier_entered", "active_courier_not_entered"].includes(row.status),
    raw: row
});

/* Vardiya listesi (/shifts/list) satırı: puantaj raporu değil, vardiya kaydının kendisi.
   Paket sayıları ve gerçekleşen saatler, vardiyanın raporu oluştuysa sunucu tarafında bu
   rapordan doldurulup geliyor. */
const normalizeDedicatedShiftRow = row => ({
    rowKey: row._id,
    date: row.date ? moment(row.date).format("DD.MM.YYYY") : null,
    projectName: [row.companyName, row.subCompanyName].filter(Boolean).join(" - ") || null,
    courier: {
        _id: row.courier?._id,
        fullname: row.courier?.fullname,
        tckn: row.courier?.tckn
    },
    city: row.city,
    plannedTime: joinTimeRange(toShiftClock(row.shiftStartTime), toShiftClock(row.shiftEndTime)),
    actualTime: joinTimeRange(toActualClock(row.actualStartTime), toActualClock(row.actualEndTime)),
    status: toShiftStatusLabel(row.status),
    plannedWorkTimeMinute: durationLabelToMinute(row.duration),
    workTimeMinute: durationLabelToMinute(row.actualDuration),
    changeReason: null,
    // Raporu olmayan vardiyada uç paket sayılarını "-" olarak döndürüyor.
    packageCount: toCount(row.packageCount),
    verifiedPackageCount: toCount(row.verifiedPackageCount),
    raw: row
});

/* Puantaj kaydında vardiya durumu ayrı bir alan değil; aktiflik + giriş bilgisinden türetiliyor. */
const resolveShiftReportStatus = row => {
    if (row.isActiveShift) {
        return row.startTime ? "active_courier_entered" : "active_courier_not_entered";
    }
    return row.status;
};

const normalizeShiftReportRow = row => ({
    rowKey: row._id,
    date: row.date ? moment(row.date, "DD-MM-YYYY").format("DD.MM.YYYY") : null,
    projectName: [row.companyName, row.subCompanyName].filter(Boolean).join(" - ") || null,
    courier: {
        _id: row.courierId || row.beforeLog?.courierId,
        fullname: row.fullname,
        tckn: row.tckn
    },
    city: row.city,
    plannedTime: joinTimeRange(row.shiftStartTime, row.shiftEndTime),
    actualTime: joinTimeRange(toActualClock(row.startTime), toActualClock(row.endTime)),
    status: toShiftStatusLabel(resolveShiftReportStatus(row)),
    // Rapor oluştuysa planlanan süre kayıtta hazır duruyor; aktif vardiya satırında rapor
    // henüz yok, o yüzden planlanan saatlerden hesaplanıyor.
    plannedWorkTimeMinute:
        toCount(row.plannedWorkTimeMinute) ??
        shiftDurationToMinute(row.shiftStartTime, row.shiftEndTime),
    workTimeMinute: row.isActiveShift
        ? elapsedWorkMinute(row.startTime)
        : toCount(row.workTimeMinute),
    changeReason: null,
    packageCount: toCount(row.packageCount),
    verifiedPackageCount: toCount(row.verifiedPackageCount),
    isActiveShift: row.isActiveShift,
    // Uç bu iki alanı metin olarak döndürüyor.
    lateStart: toCount(row.lateStart),
    earlyEnd: toCount(row.earlyEnd),
    reason: row.reason,
    raw: row
});

export const normalizeShiftRow = (row, workType) =>
    isPoolWorkType(workType) ? normalizePoolShiftRow(row) : normalizeDedicatedShiftRow(row);

export const normalizeShiftRows = (rows = [], workType) =>
    rows.map(row => normalizeShiftRow(row, workType));

// Puantaj ekranı aynı tabloyu vardiya raporlarıyla besliyor: satır şeması ortak, kaynak farklı.
export const normalizeShiftReportRows = (rows = []) =>
    rows.map(row => normalizeShiftReportRow(row));
export const toShiftReportDetailShape = row => {
    if (!row) return {};
    return {
        ...row,
        _id: row.reportId,
        reportDate: row.date ? moment(row.date).format("DD-MM-YYYY") : "",
        fullname: row.courier?.fullname || "",
        tckn: row.courier?.tckn || "",
        cellPhone: row.courier?.cellPhone || "",
        companyCode: row.companyCode || "",
        subCompanyCode: row.subCompanyCode || row.subCompanyCodeString || "",
        shiftStartTime: toShiftClock(row.shiftStartTime) || row.shiftStartTime || "",
        shiftEndTime: toShiftClock(row.shiftEndTime) || row.shiftEndTime || "",
        startTime: toActualClock(row.actualStartTime) || "",
        endTime: toActualClock(row.actualEndTime) || "",
        // Çalışma süresi rapor süresi etiketinden (ör. "02sa 15dk") dakikaya çevrilir.
        workTimeMinute: durationLabelToMinute(row.actualDuration) ?? 0,
        busyTimeMinute: toCount(row.busyTimeMinute) ?? "",
        packageCount: toCount(row.packageCount) ?? 0,
        verifiedPackageCount: toCount(row.verifiedPackageCount) ?? 0,
        shiftStartDistance: toCount(row.shiftStartDistance) ?? "",
        shiftEndDistance: toCount(row.shiftEndDistance) ?? "",
        lateStart: toCount(row.lateStart) ?? "",
        earlyEnd: toCount(row.earlyEnd) ?? "",
        startStatus: row.startStatus || "",
        endStatus: row.endStatus || "",
        endReason: row.endReason || "",
        isActiveShift: false
    };
};
